/**
 * Shared types and constants for the Proposals page search.
 *
 * Imported by both the main thread (the `useMyEvaluationsData` hook) and the
 * search worker, plus the sign-out cleanup, so it must stay dependency-free:
 * types, plain constants, and nothing that drags in React, Dexie, or MiniSearch.
 *
 * This mirrors the funder workspace's `searchTypes.ts` — see that file for the
 * canonical version of this pattern.
 */

/**
 * One indexable document: a proposal's title, or one of its field responses.
 * `version` is a cheap content fingerprint the worker diffs against the indexed
 * copy, so re-extractions (e.g. as field responses stream in) only re-index the
 * documents that actually changed instead of rebuilding the whole index.
 */
export type ProposalSearchDocument = {
  id: string;
  proposalId: string;
  label: string;
  title: string;
  text: string;
  version: string;
};

/**
 * A single MiniSearch hit returned by the worker. Carries the stored fields the
 * main thread needs to score and build a snippet — the same shape the existing
 * (synchronous) scoring/snippet logic already consumes.
 */
export type ProposalSearchHit = {
  id: string;
  score: number;
  terms: string[];
  proposalId: string;
  label: string;
  title: string;
  text: string;
};

export type ProposalSearchWorkerRequest =
  | {
      type: "init";
      roundId: string;
      /**
       * Effective (impersonated) user id. Part of the persisted-index cache key
       * so an admin's index is never restored into an impersonated session.
       */
      cacheScope: string;
    }
  | { type: "set-documents"; documents: ProposalSearchDocument[] }
  | { type: "search"; requestId: number; query: string };

export type ProposalSearchWorkerResponse =
  | {
      type: "status";
      cause: "init" | "set-documents";
      /** Documents currently in the index. */
      indexedCount: number;
      /** True when `init` restored a populated index from IndexedDB. */
      restoredFromCache: boolean;
      /** Worker time spent on this step, in ms. */
      tookMs: number;
    }
  | {
      type: "search-results";
      requestId: number;
      hits: ProposalSearchHit[];
      /** Time MiniSearch spent on this query, in milliseconds. */
      tookMs: number;
    }
  | { type: "error"; message: string };

/** Dexie database holding the persisted index (one row per round + user). */
export const PROPOSAL_SEARCH_INDEX_DB_NAME = "ProposalSearch";

/**
 * MiniSearch construction options — must match between build and `loadJSON`,
 * which is why they live here and are shared by the worker and the hook's
 * synchronous fallback. Stored fields are everything the scorer/snippet need.
 */
export const PROPOSAL_MINISEARCH_OPTIONS: {
  fields: string[];
  storeFields: string[];
} = {
  fields: ["title", "text"],
  storeFields: ["proposalId", "label", "title", "text"],
};

export const PROPOSAL_MINISEARCH_QUERY_OPTIONS = {
  prefix: true,
  fuzzy: 0.2,
  boost: { title: 8 },
  combineWith: "AND",
  weights: { prefix: 0.65, fuzzy: 0.25 },
} as const;

/**
 * Cache key for the persisted index. Bump the leading version (or change any
 * encoded parameter) and stale persisted indexes are discarded and rebuilt.
 */
export const PROPOSAL_SEARCH_INDEX_OPTIONS_KEY = [
  "v1",
  `fields=${PROPOSAL_MINISEARCH_OPTIONS.fields.join(",")}`,
  `stored=${PROPOSAL_MINISEARCH_OPTIONS.storeFields.join(",")}`,
].join(";");
